'use client';

import * as React from 'react';

import { getCityRecord, type CityCode } from '@loci/city-editions';
import { useDiscoverFacetsQuery } from '@loci/api';
import { formatElsewhereNeighborhoodLabel, isElsewhereNeighborhoodSlug } from '@loci/types';

import {
  DiscoverFacetChecklistView,
  FACET_CLEAR_ALL,
  type DiscoverFacetChecklistItem,
} from '@/components/molecules/DiscoverFacetChecklist';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { formatCount } from '@/lib/discover/formatCount';
import {
  formatDiscoverNeighborhoodHeadScope,
  resolveDiscoverScopeKey,
} from '@/lib/discover/resultsCountCopy';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import { titleCaseKebabSlug } from '@/lib/title-case-kebab-slug';

const TITLE = 'Neighborhood';

/**
 * DPL-10 (WEB-660): collapse the rail to the top six neighborhoods by
 * count, with a "show all" affordance for the rest. Charlotte currently
 * seeds 23 neighborhoods (`packages/db-core/prisma/seeds/neighborhoods/clt.json`)
 * — never hardcode that number; the expand label always derives its count
 * from the live facet payload length.
 */
const VISIBLE_NEIGHBORHOOD_LIMIT = 6;

/**
 * Custom display labels for CLT neighborhood keys that don't title-case
 * cleanly. Keyed by the lowercase slug as received from the API. The map
 * is intentionally tiny — any slug not present falls back to
 * `titleCaseKebabSlug`, which handles standard kebab-case and already-cased
 * inputs idempotently. See `NeighborhoodKey` in
 * `packages/ui/src/theme/design-tokens.ts` for the canonical CLT set.
 */
const NEIGHBORHOOD_DISPLAY_OVERRIDES: Readonly<Record<string, string>> = {
  noda: 'NoDa',
};

/**
 * Wire shape for a single neighborhood facet as emitted by the backend
 * (`services/backend-api/src/services/discoverFacets.ts:DiscoverFacetsWire`).
 * Kept local because the shared `@loci/types/DiscoverFacetCounts` type
 * historically declared `neighborhoods` as `Record<string, number>` even
 * though the wire is and always has been an array of `{slug, count}` rows.
 * The audit on 2026-05-14 caught the resulting render bug: `Object.entries`
 * on the array produced numeric-index keys (`"0"`, `"1"`) with object
 * values, which stringified as `[object Object]`.
 */
interface NeighborhoodFacetRow {
  readonly slug: string;
  readonly count: number;
}

/**
 * Render the user-facing label for a neighborhood slug. Idempotent on
 * already title-cased inputs (e.g. `"NC Music Factory"` → `"NC Music
 * Factory"`), kebab-aware (`"south-end"` → `"South End"`), and honors the
 * tiny override map for CLT-specific casing like `noda` → `NoDa`.
 */
function neighborhoodLabel(slug: string): string {
  const trimmed = slug.trim();
  if (trimmed.length === 0) return trimmed;
  const override = NEIGHBORHOOD_DISPLAY_OVERRIDES[trimmed.toLowerCase()];
  if (override) return override;
  return titleCaseKebabSlug(trimmed);
}

/**
 * Coerce the runtime facets payload into a stable `NeighborhoodFacetRow[]`.
 * The wire is `Array<{slug, count}>` per `DiscoverFacetsWire`, but the
 * shared type currently advertises `Record<string, number>`. We accept
 * either shape so a future type-definition fix doesn't have to land first,
 * and so out-of-date deploys can't 500 the rail. Rows missing a usable
 * slug are dropped to prevent `<input id="">` collisions.
 */
function toFacetRows(raw: unknown): readonly NeighborhoodFacetRow[] {
  if (Array.isArray(raw)) {
    const rows: NeighborhoodFacetRow[] = [];
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const candidate = entry as { slug?: unknown; count?: unknown };
      const slug = typeof candidate.slug === 'string' ? candidate.slug.trim() : '';
      if (slug.length === 0) continue;
      const count =
        typeof candidate.count === 'number' && Number.isFinite(candidate.count)
          ? candidate.count
          : 0;
      rows.push({ slug, count });
    }
    return rows;
  }
  if (raw && typeof raw === 'object') {
    const rows: NeighborhoodFacetRow[] = [];
    for (const [slug, count] of Object.entries(raw as Record<string, unknown>)) {
      const trimmedSlug = slug.trim();
      if (trimmedSlug.length === 0) continue;
      const safeCount = typeof count === 'number' && Number.isFinite(count) ? count : 0;
      rows.push({ slug: trimmedSlug, count: safeCount });
    }
    return rows;
  }
  return [];
}

/**
 * Convert wire facet rows into the `DiscoverFacetChecklistItem[]` shape
 * consumed by the View. Labels are derived from the slug via
 * `neighborhoodLabel`. Items are sorted by count descending (DPL-10 —
 * "top six" is a count ranking, not an alphabetical one) with a stable
 * secondary sort by label so ties never reorder the list from one fetch to
 * the next.
 *
 * COR-125 — the backend's `elsewhere` catch-all ("Greater Charlotte":
 * events at venues outside every neighborhood) is one more row, labelled from
 * the city record rather than title-cased from its slug, and always sorted
 * LAST: it is the residual that makes the rail add up to the results total,
 * not a place to rank against the named neighborhoods.
 */
function buildItemsFromFacets(
  rows: readonly NeighborhoodFacetRow[],
  cityCode: CityCode,
): DiscoverFacetChecklistItem[] {
  const elsewhereLabel = formatElsewhereNeighborhoodLabel(getCityRecord(cityCode)?.displayName);
  return rows
    .map((row) => ({
      slug: row.slug,
      label: isElsewhereNeighborhoodSlug(row.slug) ? elsewhereLabel : neighborhoodLabel(row.slug),
      count: row.count,
    }))
    .sort(
      (a, b) =>
        Number(isElsewhereNeighborhoodSlug(a.slug)) - Number(isElsewhereNeighborhoodSlug(b.slug)) ||
        b.count - a.count ||
        a.label.localeCompare(b.label),
    );
}

/**
 * Formats the DPL-10 "show all" / "show less" affordance beneath the
 * top-six collapsed list. `totalCount` is always the live facet payload
 * length (`items.length`), never a hardcoded value — Charlotte has 23
 * neighborhoods today, but that count changes the moment one is added.
 * `totalCount` includes the COR-125 `elsewhere` row, so the label always
 * names exactly how many rows expanding reveals.
 */
export function formatNeighborhoodExpandLabel(totalCount: number, expanded: boolean): string {
  if (expanded) return 'Show less';
  const noun = totalCount === 1 ? 'neighborhood' : 'neighborhoods';
  return `Show all ${formatCount(totalCount)} ${noun}`;
}

/**
 * Return a stable DOM id for a neighborhood checkbox input. The
 * `neighborhood-` prefix namespaces away from the Category filter and any
 * other surface that adopts the same View. The slug is encoded via
 * `encodeURIComponent` because WHATWG HTML forbids ASCII whitespace in
 * `id` attributes (slugs like `"NC Music Factory"` would otherwise produce
 * non-conforming ids that break `<label for>` referencing and CSS id
 * selectors). Empty slugs are filtered out upstream in `toFacetRows`, so
 * this always returns a non-empty id.
 */
function neighborhoodInputId(item: DiscoverFacetChecklistItem): string {
  return `neighborhood-${encodeURIComponent(item.slug)}`;
}

/**
 * Return the visually-hidden `aria-label` text for a row's checkbox.
 * Reads "<label> (<count> events)" so screen-reader users get the same
 * count signal as sighted users.
 */
function neighborhoodAriaLabel(item: DiscoverFacetChecklistItem): string {
  const noun = item.count === 1 ? 'event' : 'events';
  return `${item.label} (${item.count} ${noun})`;
}

export interface DiscoverNeighborhoodFilterProps {
  /**
   * Active city — used to scope the facets query to the city geo anchor
   * (WEB-263). Without this, neighborhood counts are computed globally and
   * include cross-country events.
   */
  readonly cityCode: CityCode;
  /** Wrapper class names forwarded to the underlying view. */
  readonly className?: string;
}

/**
 * Connected wrapper binding `DiscoverFacetChecklistView` to the Neighborhood
 * facet. Reads `facets.neighborhoods` from `useDiscoverFacetsQuery` (M7.3)
 * and `params.neighborhood` from `useDiscoverParams` (M1.1). Toggling a row
 * writes the slug onto the comma-joined `?neighborhood=` URL param; the
 * header "Clear" link wipes the param entirely.
 *
 * Resilience contract:
 * - Healthy facets API → render every neighborhood the API surfaces (top six
 *   by count, ties broken alphabetically — see the Truncation note below),
 *   with live counts.
 * - Loading → skeleton rows (WEB-786). "No options available" is a measured
 *   empty, not a pending claim.
 * - API error → empty list + "No options available". Unlike Category, there
 *   is no canonical client-side fallback list — neighborhoods are sourced
 *   exclusively from the backend.
 *
 * **Request coalescing (API-1428):** The chip counts come from the SAME
 * `useDiscoverFacetsQuery` cache entry the list view subscribes to. Both
 * derive their args from the shared {@link buildDiscoverQueryArgs} helper, so
 * RTK Query dedupes this facet-rail subscription onto the list view's
 * in-flight `/events/discover` request instead of firing a second one. The
 * previous implementation passed a city-anchor-only args object (no `view`/
 * `sort`/`date`/…), which produced a different cache key and burned an extra
 * anon-IP request on every cold load (API-1416 audit follow-up).
 *
 * Wire-shape note (WEB-264): The backend emits `Array<{slug, count}>` for
 * `facets.neighborhoods`. The shared `DiscoverFacetCounts` type
 * historically advertised `Record<string, number>`, which caused
 * `Object.entries` here to iterate numeric-index keys and stringify the
 * object values as `[object Object]`. `toFacetRows` accepts either shape
 * defensively so the sidebar never renders `[object Object]` again,
 * regardless of which side fixes its type definition first.
 *
 * Truncation (WEB-660 / DPL-10): the rail collapses to the top six
 * neighborhoods by count (ties broken alphabetically), with a "show all"
 * affordance for the rest. A neighborhood the viewer already has selected
 * via URL state stays visible even if its count later drops it out of the
 * top six — `DiscoverFacetChecklistView`'s `visibleLimit` handles that by
 * keeping any selected item that would otherwise be truncated.
 */
export function DiscoverNeighborhoodFilter({
  cityCode,
  className,
}: DiscoverNeighborhoodFilterProps): React.ReactElement {
  const { params, setParams } = useDiscoverParams();
  // Build the args identically to the list view (`DiscoverListView` /
  // `DiscoverResultsBarConnected`) so RTK Query collapses this facet-rail
  // subscription onto the list view's single `/events/discover` request
  // instead of issuing a parallel one (API-1428).
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData, isError } = useDiscoverFacetsQuery(queryArgs);

  const items = React.useMemo<DiscoverFacetChecklistItem[]>(() => {
    if (!currentData?.facets.neighborhoods || isError) {
      return [];
    }
    return buildItemsFromFacets(toFacetRows(currentData.facets.neighborhoods as unknown), cityCode);
  }, [currentData, isError, cityCode]);
  const countsPending = !isError && currentData?.facets.neighborhoods === undefined;

  const handleToggle = React.useCallback(
    (slug: string) => {
      if (slug === FACET_CLEAR_ALL) {
        setParams({ neighborhood: [] });
        return;
      }
      const current = params.neighborhood;
      const next = current.includes(slug)
        ? current.filter((value) => value !== slug)
        : [...current, slug];
      setParams({ neighborhood: next });
    },
    [params.neighborhood, setParams],
  );

  const handleClear = React.useCallback(() => {
    setParams({ neighborhood: [] });
  }, [setParams]);

  const headScope = formatDiscoverNeighborhoodHeadScope(
    resolveDiscoverScopeKey(currentData?.facets?.scopeKey, params.date),
  );

  return (
    <DiscoverFacetChecklistView
      title={TITLE}
      id="discover-neighborhood-filter"
      items={items}
      selected={params.neighborhood}
      onToggle={handleToggle}
      onClear={handleClear}
      inputIdForItem={neighborhoodInputId}
      ariaLabelForItem={neighborhoodAriaLabel}
      visibleLimit={VISIBLE_NEIGHBORHOOD_LIMIT}
      formatExpandLabel={formatNeighborhoodExpandLabel}
      countsPending={countsPending}
      headScope={headScope}
      className={className}
    />
  );
}
